// Powerlete — post-load warmer for lazy slides in a horizontal scroller.
//
// Two emit sites, one behaviour:
// - PDP gallery (product-media-gallery-content.liquid): PR #735 made every gallery image
//   past the first in each subtree loading="lazy" to keep them out of the LCP window
//   (they were ALL eager before, ~12 full-priority fetches competing with the hero).
// - Product rails (resource-list-carousel.liquid): commit 4e36f50 (2026-07-06) bounded the
//   card eager-image flood, so only each card's first image in sections at index <= 3 is
//   eager — rails below that, and every hover/extra card media, are lazy.
//
// Both were correct for the metric, but changed the swipe feel: native lazy in a
// horizontal scroller only fetches a slide as it nears the viewport, so a quick swipe
// outran the network and shoppers watched images pop in mid-gesture.
//
// This element restores the pre-#735 "everything is already there" swipe WITHOUT giving
// back the LCP win: it upgrades the carousel's lazy images to eager only AFTER the load
// event — outside the window LCP/FCP/TBT measure — at idle, or on the first touch of the
// scroller if that comes sooner. No trigger can warm before load; every path defers.
//
// Scope and guards:
// - Only the slideshow (carousel) subtree of its host. On the PDP the desktop grid scrolls
//   vertically, where native lazy already fetches ahead correctly; warming it would just
//   re-create the hidden-subtree double-fetch #735 removed.
// - Only when the scroller is actually visible (clientWidth > 0). The PDP's two subtrees
//   are always in the DOM and CSS hides one per breakpoint; on desktop the carousel is the
//   hidden one and must stay cold. If the viewport later crosses the breakpoint, the
//   interaction listeners still warm it on first touch.
// - Respects Save-Data: metered connections keep pure on-demand loading (first-touch warm
//   still applies, since at that point the shopper has asked for the images).
//
// Self-initialises via connectedCallback, so it re-runs when the gallery is re-rendered by
// the Section Rendering API (variant/colour changes bring new media).
class PwGalleryWarm extends HTMLElement {
  connectedCallback() {
    // Root: the PDP gallery when nested in <media-gallery>; otherwise the immediate
    // parent (the rail's .resource-list-carousel-wrapper — the element must be emitted
    // as a direct child of the container that holds the scroller).
    this.root = this.closest('media-gallery') || this.parentElement;
    this.scroller = this.root && this.root.querySelector('slideshow-slides');
    if (!this.scroller) return;

    // Every trigger funnels through warmIfVisible: the scroller-origin events can only
    // fire while it is visible anyway, and slideshow:select also reaches here bubbling
    // from the zoom dialog's slideshow (nested in the PDP's <media-gallery>), which on
    // desktop must not warm the hidden carousel subtree.
    this._warm = this.warmIfVisible.bind(this);
    // First touch/scroll of the scroller = the shopper is about to need every slide.
    this.scroller.addEventListener('pointerdown', this._warm, { passive: true, once: true });
    this.scroller.addEventListener('scroll', this._warm, { passive: true, once: true });
    // Arrows / thumbnails / dots / autoplay advance slides without touching the scroller.
    this._onSelect = this._warm;
    this.root.addEventListener('slideshow:select', this._onSelect);

    // Idle warm, after load so it can never sit inside the LCP window.
    if (!(navigator.connection && navigator.connection.saveData)) {
      this._idle = () => {
        if ('requestIdleCallback' in window) {
          this._idleId = requestIdleCallback(() => this.warmIfVisible(), { timeout: 3000 });
        } else {
          this._timerId = setTimeout(() => this.warmIfVisible(), 1500);
        }
      };
      if (document.readyState === 'complete') {
        this._idle();
      } else {
        window.addEventListener('load', this._idle, { once: true });
      }
    }
  }

  disconnectedCallback() {
    if (this.scroller) {
      this.scroller.removeEventListener('pointerdown', this._warm);
      this.scroller.removeEventListener('scroll', this._warm);
    }
    if (this.root && this._onSelect) this.root.removeEventListener('slideshow:select', this._onSelect);
    if (this._idle) window.removeEventListener('load', this._idle);
    if (this._idleId && 'cancelIdleCallback' in window) cancelIdleCallback(this._idleId);
    if (this._timerId) clearTimeout(this._timerId);
  }

  warmIfVisible() {
    if (this._warmed) return;
    // A deferred load-listener can outlive a Section Rendering re-render; never warm a
    // detached gallery (the replacement instance has its own warmer).
    if (!this.isConnected) return;
    // Never warm inside the load window, whatever the trigger: a variant-linked media
    // selection fires slideshow:select (and a programmatic scroll) during init, and
    // warming there would put every gallery image back in the LCP window -- the exact
    // regression #735 fixed. Deferring to load costs nothing real: pre-load, native
    // lazy is already fetching whichever slide the shopper is near. The deferred call
    // re-enters HERE, not warm(), so the visibility check still applies at fire time.
    if (document.readyState !== 'complete') {
      if (!this._deferred) {
        this._deferred = true;
        window.addEventListener('load', () => this.warmIfVisible(), { once: true });
      }
      return;
    }
    // Hidden subtree (desktop) stays cold; see the header comment.
    if (this.scroller.clientWidth > 0) this.warm();
  }

  warm() {
    if (this._warmed) return;
    this._warmed = true;
    // On the PDP, this.scroller's images ARE the top-level slideshow-component's own
    // slides (no nesting) and every one of them is exactly what pw-gallery-warm exists to
    // restore for swipe smoothness, so those still warm unconditionally, as before.
    //
    // In a rail, every card is itself a slide, and each card renders a FURTHER-nested
    // slideshow-component for card-gallery's own hover/extra-image carousel. Those nested
    // images are NOT what the rail's swipe feel needs -- only the shopper hovering a
    // specific card would ever see slide 2+ of ITS gallery. Warming them unconditionally
    // pulled every hidden card's hover slides too: 80 images / 6.5MB on the homepage at
    // 375px, for images most shoppers never trigger (audit PERF-02). So for a nested
    // slideshow-component only, follow slideshow-slide.liquid's own aria-hidden marker
    // (false only on index 0 / the current slide) and skip its hover-only extras.
    const topSlideshow = this.scroller.closest('slideshow-component');
    // Setting .loading to 'eager' on a deferred lazy image starts its fetch immediately
    // (HTML spec: "will lazy load" state resolves when the attribute switches to eager).
    const images = this.scroller.querySelectorAll('img[loading="lazy"]');
    for (const img of images) {
      if (img.closest('slideshow-component') !== topSlideshow) {
        const slide = img.closest('slideshow-slide');
        if (slide && slide.getAttribute('aria-hidden') === 'true') continue;
      }
      img.loading = 'eager';
    }
  }
}

if (!customElements.get('pw-gallery-warm')) {
  customElements.define('pw-gallery-warm', PwGalleryWarm);
}
